import { MarkerData, MarkerGroup } from 'app/common/components/geo-manager/geo-manager.interfaces';
import { ExtendesBusinessPartner, ExtendsTrips, ExtendsVisits, FrecuencyData, GeoSource, GroupData, ShipToData, VisitData, VisitsData } from '../models/map-modal.interface';
import { DateHourPipe } from 'app/pipes/formated-date.pipe';
import { FormatOtherDateHourPipe } from 'app/pipes/other-date.pipe';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';
import { MonitorDocumentTypeEnum } from '../models/map-modal.enum';
import { OrderAttributes, TripAttributes } from '@albatrosdeveloper/ave-models-package';
import { statusIdTrip } from './map-modal.util';

function extractCustomProps(shipTo: ExtendesBusinessPartner): Record<string, any> {
  const address = shipTo?.addresses?.[0]?.address || shipTo?.principalAddress?.address || '-';
  
  const contacto =
    shipTo?.addresses?.[0]?.contacts?.[0]?.fullName ||
    shipTo?.contacts?.[0]?.contact?.fullName ||
    shipTo?.principalContact?.fullName || '-';
  
  const telefono =
    shipTo?.addresses?.[0]?.phones?.[0]?.phone ||
    shipTo?.phones?.[0]?.phone ||
    shipTo?.principalPhone?.phone ||
    '-';

  return {
    'Nombre Legal': shipTo?.name || '-',
    'Nombre Comercial': shipTo?.commercialName || '-',
    'RTN': shipTo?.NIT || '-',
    'Identidad': shipTo?.DNI || '-',
    'Dirección principal': address,
    'Contacto': contacto,
    'Teléfono': telefono,
  };
}

function extractVisitCustomProps(p: VisitData): Record<string, any>{
  const dateHourPipe = new DateHourPipe();
  return {
    'Socio de Negocio': p?.shipToName || '-',
    'Visita': p?.reasonForVisitName || '-',
    'Nro. Doc': p?.serials || '-',
    'Visita Válida': p?.validVisit ? 'SI' : 'No',
    'Zona': p?.wasTheVisitOnSite ? 'SI' : 'NO',
    'Duración Visita': dateHourPipe.transform(p?.duration ?? 0),
  };
}

function buildMarkersFromPoints(shipTo: ExtendesBusinessPartner, points: GeoSource[]): MarkerGroup[] {
  const customProps = extractCustomProps(shipTo);

  return points
    .filter(p => p.point?.length === 2)
    .map(p => ({
      groupName: p.groupName,
      groupColor: p.groupColor,
      markers: [{
        title: p.title,
        latitude: Number(p.point[0]),
        longitude: Number(p.point[1]),
        customProperties: customProps
      }]
    }));
}

// APP + ERP
export function buildMarkerSimpleTwo(element: ExtendsVisits | VisitData): MarkerGroup[] {
  const title = 'shipTo' in element ? element.shipTo.name || '-' : element.shipToName || '-';

  // APP Point
  const appPoint = element.geoPoint?.map(coord => Number(coord)) as [number, number] | undefined;

  // ERP Point solo si es ExtendsVisits y hay addresses
  let erpPoint: [number, number] | undefined;
  if ('shipTo' in element && element.shipTo?.addresses?.[0]?.geolocationPoint) {
    erpPoint = element.shipTo.addresses[0].geolocationPoint.map(coord => Number(coord)) as [number, number];
  }

  let addressPoint: [number, number] | undefined;
  if (!('shipTo' in element) && (element as any).address?.[0]?.geolocationPoint) {
    addressPoint = (element as any).address[0].geolocationPoint.map((coord: any) => Number(coord)) as [number, number];
  }

  const points: GeoSource[] = [];
  if (appPoint) points.push({ point: appPoint, groupName: 'APP', groupColor: 'green', title });
  if (erpPoint) points.push({ point: erpPoint, groupName: 'AVE', groupColor: 'blue', title });
  if (addressPoint) points.push({ point: addressPoint, groupName: 'AVE', groupColor: 'blue', title });

  // Si hay shipTo, construir con buildMarkersFromPoints
  if ('shipTo' in element && element.shipTo) {
    return buildMarkersFromPoints(element.shipTo, points);
  } 

  // Solo si es VisitData
  if (!('shipTo' in element)) {
    const customProps = extractVisitCustomProps(element);
    return points.map(p => ({
      groupName: p.groupName,
      groupColor: p.groupColor,
      markers: [{
        title: p.title,
        latitude: p.point ? p.point[0] : 0,
        longitude: p.point ? p.point[1] : 0,
        customProperties: customProps
      }]
    }));
  }

  return [];
}

// Solo un punto
export function buildMarkerSimple(element: ExtendesBusinessPartner): MarkerGroup[] {
  const groupName = element.types?.[0]?.name || element.name || '-';
  const geoPoint = element.addresses?.[0]?.geolocationPoint || element.principalAddress?.geolocationPoint;

  const points: GeoSource[] = [
    { point: geoPoint, groupName: groupName, groupColor: 'green', title: element.name || ''}
  ];

  return buildMarkersFromPoints(element, points);
}

//visita
export function buildMarkerVisitRoute(element: VisitsData, isflagAVE: boolean): MarkerGroup[] {
  if (!element.visits) return [];

  const allMarkers: MarkerGroup[] = [];
  element.visits.forEach((p, index) => {
    const visitMarkers: MarkerData[] = [];

    // APP
    if (p.geoPoint?.length === 2) {
      const customProps = extractVisitCustomProps(p);
      visitMarkers.push({
        title: `Visita ${index + 1}`,
        latitude: Number(p.geoPoint[0]),
        longitude: Number(p.geoPoint[1]),
        order: index + 1,
        customProperties: customProps,
      });

      allMarkers.push({
        groupName: 'Visita',
        groupColor: 'green',
        markers: visitMarkers,
      });
    }

    // AVE si isflagAVE está activado y la visita tiene dirección con geolocalización
    if (isflagAVE && p.address?.[0]?.geolocationPoint?.length === 2) {
      const addressPoint = p.address[0].geolocationPoint.map((coord: any) => Number(coord)) as [number, number];
      const customProps = extractVisitCustomProps(p);

      allMarkers.push({
        groupName: 'Visita AVE',
        groupColor: 'blue',
        markers: [{
          title: `AVE - Visita ${index + 1}`,
          latitude: addressPoint[0],
          longitude: addressPoint[1],
          order: index + 1,
          customProperties: customProps,
        }],
      });
    }
  });

  return allMarkers;
}

//agrupado
export function buildMarkerGroups(groupShipTo: GroupData[], documentType: MonitorDocumentTypeEnum, isDate: boolean = false): MarkerGroup[] {
  const dateHourPipe = new FormatOtherDateHourPipe();

  //Función para construir propiedades comunes
  const buildCustomProps = (shipTo: ShipToData) => {
    const customProps = {
      'Nombre Legal': shipTo?.name || '-',
      'Nombre Comercial': shipTo?.commercialName || '-',
      'RTN': shipTo?.NIT || '-',
      'Identidad': shipTo?.DNI || '-',
      'Dirección principal': shipTo?.addresses?.[0]?.address || '-',
      'Contacto': shipTo?.contacts?.[0]?.contact?.fullName || '-',
      'Teléfono': shipTo?.phones?.[0]?.phone || '-',
    };

    const orders = shipTo.orders || [];
    orders.forEach((order, index) => {
      customProps[`Nro. Doc${orders.length > 1 ? ` #${index + 1}` : ''}`] = {
        isDocument: true,
        type: order.type?.code || '',
        serial: order.serial || '-',
        ...(order.sourceTransactions ? { sourceTransactions: order.sourceTransactions } : {})
      };
    });

    return { customProps, orders };
  };

  //Función para crear marcador
  const buildMarker = (shipTo: ShipToData, lat: number, lng: number, orders: OrderAttributes[], customProps: any,orderIndex?: number) => {
    const marker: MarkerData = {
      title: shipTo?.commercialName || shipTo?.name || '-',
      latitude: Number(lat),
      longitude: Number(lng),
      customProperties: customProps,
    };

    if (isDate && orders.length > 0) {
      marker.description = 'Fecha y hora de evento: ' + dateHourPipe.transform(orders[0].creationDateTime, true);
    }

    if(orderIndex){
      marker.order = orderIndex
    }

    return marker;
  };

  return groupShipTo
    .map(group => ({
      groupName: group.group.name || 'Sin grupo',
      markers: (group.shipTo || []).flatMap(shipTo => {
        if (documentType === MonitorDocumentTypeEnum.VISIT) {
          return (shipTo.visits || [])
            .filter(v => v?.geoPoint?.length > 0)
            .map((v, index) => {
              const { customProps, orders } = buildCustomProps(shipTo);
              return buildMarker(shipTo, Number(v.geoPoint[0]), Number(v.geoPoint[1]), orders, customProps, index+1);
            });
        } else {
          return (shipTo.orders || [])
            .filter(o => o?.address?.geolocationPoint?.length > 0)
            .map(o => {
              const { customProps, orders } = buildCustomProps(shipTo);
              return buildMarker(shipTo, o.address.geolocationPoint[0], o.address.geolocationPoint[1], orders, customProps);
            });
        }
      })
    }))
    .filter(group => group.markers.length > 0);
}

//frecuency
export function buildMarkerFrecuencyGroups(frecuencyData: FrecuencyData[]): MarkerGroup[] {
  return frecuencyData
    .map(groupData => {
      const markers = groupData.shipTo
        .filter(ship => ship.principalAddress?.geolocationPoint?.length === 2)
        .map(ship => {
          const customProps = {
            'Nombre Legal': ship?.name || '-',
            'Nombre Comercial': ship?.commercialName || '-',
            'Dirección principal': ship?.principalAddress?.address || '-',
            'Contacto': ship?.principalContact?.fullName || '-',
            'Teléfono': ship?.principalPhone?.phone || '-',
          };
          
          return {
            title: ship?.commercialName || ship?.name || '-',
            latitude: ship.principalAddress!.geolocationPoint[0],
            longitude: ship.principalAddress!.geolocationPoint[1],
            customProperties: customProps
          };
        });

      return {
        groupName: groupData.group.name,
        markers
      };
    })
    .filter(group => group.markers.length > 0);
}

//Trip visit monitor global 
export function buildMarkerVisitTripGroups(element: ExtendsTrips[]): MarkerGroup[]{
  return element
    .map(trip => {
      const markers = (trip.visits || [])
        .filter(v => v?.geoPoint?.length === 2) 
        .map(v => {
          const contacto =
            v.shipTo?.addresses?.[0]?.contacts?.[0]?.fullName || '-';
          
          const telefono =
            v.shipTo?.addresses?.[0]?.phones?.[0]?.phone || '-';

          const customProps = {
            'Nombre Legal': v.shipTo?.name || '-',
            'Nombre Comercial': '-',//v.shipTo?.commercialName || '-',
            'RTN': '-', //v.shipTo?.NIT || '-',
            'Identidad': '-', //v.shipTo?.DNI || '-',
            'Dirección principal': v.shipTo?.addresses?.[0]?.address || '-',
            'Contacto': contacto,
            'Teléfono': telefono,
          };

          return {
            title: v.shipTo?.name || '',
            latitude: Number(v.geoPoint[0]),
            longitude: Number(v.geoPoint[1]),
            customProperties: customProps
          };
        });

      const tripStatus = statusIdTrip(trip.statusTripId);
      return {
        groupName: trip.id ? `Viaje ${trip.id}` : 'Sin grupo',
        groupColor: tripStatus.color,
        markers
      }
    })
    .filter(group => group.markers.length > 0);
}